import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _reduce from 'lodash/reduce';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import { gqlCountryEnum as COUNTRY } from '@minecraft.graphql-types';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import ErrorMessage from '@minecraft.error-message';
import { useTranslation } from '@blocs.i18n';
import { QUERY_DMAS } from '../../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { FiltersNameEnum } from '../../../shared/types';
import { getLocations } from '../../../shared/utils';
import { FormItem } from '../../../shared/styles';
import { FilterDrawerType, FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { useFiltersLabel } from '../../shared/hooks/useFiltersLabel';
import { WorkingLocations } from '../../FilterDrawerCollection';

interface Props {
  type: FilterDrawerType;
}

const WorkingLocationsCategory: FC<Props> = ({ type }) => {
  const { t } = useTranslation();

  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_DMAS);

  const { isCategoryMatchedSearch, assignOptionsToCategoryFilters } = useDrawerSearchFilterNameOrCategory(
    FiltersCategoryNameEnum.workingLocations
  );
  const { filtersCounts } = useDrawerFiltersCounts();

  const workingLocationList = useMemo(() => {
    const { usLocations, caLocations, euLocations, auLocations, additionalLocations, laLocations } = getLocations({
      dmas: data?.dmas || [],
    });
    const workingLocation = [
      ...(usLocations?.length > 0
        ? [{ id: COUNTRY.US, name: t('common:label.country.unitedStates'), options: usLocations }]
        : []),
      ...(caLocations?.[0]?.cities?.length > 0
        ? [{ id: COUNTRY.CA, name: t('common:label.country.canada'), options: caLocations?.[0]?.cities }]
        : []),
      ...(euLocations?.length > 0
        ? [
            {
              id: `${[COUNTRY.GB, COUNTRY.IE]}`,
              name: t('common:label.country.unitedKingdomIreland'),
              options: euLocations,
            },
          ]
        : []),
      ...(auLocations?.length > 0
        ? [
            {
              id: `${[COUNTRY.AU, COUNTRY.NZ]}`,
              name: t('common:label.country.australiaNewZealand'),
              options: auLocations,
            },
          ]
        : []),
      ...(laLocations?.length > 0
        ? [
            {
              id: `${[COUNTRY.MX, COUNTRY.BR, COUNTRY.VE, COUNTRY.PE, COUNTRY.CL, COUNTRY.AR, COUNTRY.CO]}`,
              name: t('common:label.country.latinAmerica'),
              options: laLocations,
            },
          ]
        : []),
      ...(additionalLocations?.length > 0
        ? [{ id: 'ADDITINAL', name: t('common:label.addtionalLocations'), options: additionalLocations }]
        : []),
    ];

    const generateNestedOptions = (options) => {
      return _reduce(
        options,
        (acc, option) => {
          const optionCount =
            _find(filtersCounts?.[FiltersCountNameEnum.workingLocationIds], { key: option.id })?.docCount || 0;

          return [
            ...acc,
            {
              value: option.id,
              label: option.name,
              ...((option?.options || option?.cities) && {
                options: generateNestedOptions(option?.options || option?.cities),
              }),
              ...(!option?.options &&
                !option?.cities && {
                  count: optionCount,
                }),
            },
          ];
        },
        []
      );
    };

    return generateNestedOptions(_filter(workingLocation, (location) => location.options));
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (workingLocationList.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.workingLocation]: workingLocationList,
      });
    }
  }, [workingLocationList]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <FormItem $fullWidth $marginValue={2}>
        <WorkingLocations
          name={FiltersNameEnum.workingLocation}
          label={filtersLabelOfAudience[FiltersNameEnum.workingLocation]}
        />
      </FormItem>
    )
  );
};

export default WorkingLocationsCategory;
